import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { rotaInicial } from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { Artefato } from "../../components/Artefato.js";
import { Link, Navigate } from "react-router-dom";
import { Botao, Campo, Entrada, Falha } from "../../components/ui.js";
import { BotaoGoogle } from "./BotaoGoogle.js";
import { useAuth } from "./useAuth.js";

/**
 * A tela de entrada.
 *
 * O `useAuth` aqui não é conveniência: é o que faz a tela **sair** depois que a
 * entrada dá certo. Tanto o formulário quanto o botão do Google terminam em
 * `invalidateQueries()`, e invalidar só refaz query que está montada — sem
 * ninguém observando `auth.me` nesta página, o login tinha sucesso no servidor e
 * a tela ficava parada no formulário.
 *
 * Enquanto o login morava dentro do `RequireAuth` isso vinha de graça, porque é
 * ele quem observa. A rota pública `/entrar` a tirou de lá, e com ela o
 * observador.
 */
export function LoginPage() {
  const { autenticado, papel, carregando } = useAuth();
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const login = trpc.auth.login.useMutation({
    onSuccess: async () => {
      setErro(null);
      await queryClient.invalidateQueries();
    },
    onError: (e) => setErro(e.message),
  });

  // Já entrou: nesta mesma tela, logo depois do login, ou chegando aqui com
  // sessão viva. `rotaInicial` e não `/nova` fixo — um quiosque vai projetar.
  if (!carregando && autenticado) return <Navigate to={rotaInicial(papel)} replace />;

  function enviar(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    login.mutate({ email: email.trim().toLowerCase(), password: senha });
  }

  return (
    <main className="flex min-h-full flex-col justify-center px-6 py-12">
      <div className="mx-auto w-full max-w-sm">
        {/* Marca: a mesma geometria 40/60 da peça que o sistema fabrica, em
            escala pequena e sem rótulos — aqui ela identifica, não instrui. */}
        <div className="flex items-center gap-3.5">
          <Artefato tamanho="tira" estado="vazio" rotulos={false} className="h-14 [&>div]:h-14" />
          <div>
            <h1 className="font-display text-[1.75rem] leading-none tracking-[-0.02em]">
              Foto Interativa
            </h1>
            <p className="mt-1.5 text-[0.9375rem] text-texto-suave">
              Entre para começar a atender.
            </p>
          </div>
        </div>

        <form onSubmit={enviar} className="mt-8 flex flex-col gap-4">
          <Campo rotulo="E-mail">
            <Entrada
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              required
              disabled={login.isPending}
            />
          </Campo>

          <Campo rotulo="Senha">
            <Entrada
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="current-password"
              required
              disabled={login.isPending}
            />
          </Campo>

          {erro && <Falha mensagem={erro} />}

          <Botao type="submit" tom="brasa" largo carregando={login.isPending} className="mt-2">
            Entrar
          </Botao>
        </form>

        <div className="mt-5">
          <BotaoGoogle />
        </div>

        <p className="mt-6 text-center text-[0.875rem]">
          <Link to="/recuperar" className="text-texto-suave underline underline-offset-4">
            Esqueci minha senha
          </Link>
        </p>

        <p className="mt-3 text-center text-[0.875rem] text-texto-suave">
          Ainda não tem conta?{" "}
          <Link to="/cadastro" className="text-brasa underline underline-offset-4">
            Criar conta
          </Link>
        </p>
      </div>
    </main>
  );
}
